import { StyleSheet, View } from 'react-native';
import { TabBar, TabBarItem } from 'react-native-tab-view';
import { Colors, Typography } from '@banhala/ui-kit';
import { isNotNil } from 'shared/src/utils';
import { TabSlotText } from './TabSlotText';
import { TabOverlayImage } from './TabOverlayImage';
import type { HomeTabRoute } from '../HomeContents';
import type { SceneRendererProps, NavigationState } from 'react-native-tab-view';

type HomeTabBarProps = SceneRendererProps & {
  navigationState: NavigationState<HomeTabRoute>;
};

export const HomeTabBar = (props: HomeTabBarProps) => {
  const { navigationState } = props;
  const currentRoute = navigationState.routes[navigationState.index];

  return (
    <TabBar
      {...props}
      tabStyle={styles.tab}
      scrollEnabled={true}
      gap={16}
      contentContainerStyle={styles.tabBarContent}
      renderTabBarItem={tabItemProps => {
        const render = tabItemProps.route.render;
        const isFocused =
          tabItemProps.navigationState.index ===
          tabItemProps.navigationState.routes.indexOf(tabItemProps.route);

        const overlayImage = render?.overlay_image;
        if (isNotNil(overlayImage)) {
          const source = isFocused
            ? overlayImage.selected_source
            : overlayImage.unselected_source;
          const imageUrl = source?.url;
          const imageWidth = overlayImage.width ?? 48;
          const imageHeight = overlayImage.height ?? 44;

          if (isNotNil(imageUrl)) {
            return (
              <TabBarItem
                {...tabItemProps}
                key={tabItemProps.route.key}
                label={() => (
                  <TabOverlayImage
                    uri={imageUrl}
                    width={imageWidth}
                    height={imageHeight}
                    animation={source?.animation ?? null}
                  />
                )}
              />
            );
          }
        }

        const activeColor =
          render?.selected_text_color ?? Colors.content_primary;
        const inactiveColor =
          render?.text_color ?? Colors.content_secondary;

        const overlayTextUnselected =
          render?.overlay_text?.unselected ?? null;

        return (
          <View style={styles.tabItemWrapper}>
            {isNotNil(overlayTextUnselected) && !isFocused && (
              <View style={styles.slotTextWrapper}>
                <TabSlotText overlayText={overlayTextUnselected} />
              </View>
            )}
            <TabBarItem
              {...tabItemProps}
              key={tabItemProps.route.key}
              activeColor={activeColor}
              inactiveColor={inactiveColor}
              label={({ focused, color }) => (
                <Typography
                  type={focused ? 'subtitle1.5' : 'body1.5'}
                  rawColor={color}
                >
                  {tabItemProps.route.title}
                </Typography>
              )}
              badge={
                render?.is_ad
                  ? () => (
                      <View style={styles.adBadge}>
                        <Typography type="subtitle7" rawColor="#999">
                          AD
                        </Typography>
                      </View>
                    )
                  : undefined
              }
            />
          </View>
        );
      }}
      indicatorStyle={[
        styles.indicator,
        {
          backgroundColor:
            currentRoute?.render?.selected_text_color ??
            Colors.content_primary,
        },
      ]}
      style={styles.tabBar}
    />
  );
};

const styles = StyleSheet.create({
  tabBar: {
    height: 44,
    backgroundColor: Colors.bg_always_white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border_tertiary,
    elevation: 0,
    shadowOpacity: 0,
  },
  tabBarContent: {
    paddingHorizontal: 16,
  },
  tab: {
    width: 'auto',
    padding: 0,
    paddingHorizontal: 2,
    height: 44,
    minHeight: 44,
  },
  indicator: {
    height: 2,
  },
  tabItemWrapper: {
    position: 'relative',
  },
  slotTextWrapper: {
    position: 'absolute',
    top: 2,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 1,
  },
  adBadge: {
    position: 'absolute',
    top: 4,
    right: -8,
  },
});
